import { MissionEnumsLists, TaskEnums, TaskEnumsLists } from "../../../../../common/constants";
import { GeoJsonPoint } from "../../../../../common/maphelpers";
import { GetTaskDriverState, GetTaskStateBackgroundColor } from "../../../../../common/mission";
import Route from "../../../../../common/Route";
import MapPlugin from "../MapPlugin";
import { Checkbox, Divider, FormControl, IconButton, InputLabel, ListItemText, MenuItem, Select, Stack } from "@mui/material";
import LayerSettings from "../../common/LayerSettings";
import { useCallback, useEffect, useState } from "react";
import { useOperationsMap } from "../../OperationsMapProvider";
import { useTranslation } from "react-i18next";
import { FilterListOff as ClearIcon, OutlinedFlag as FlagIcon } from "@mui/icons-material";

export default class DispatchLayer extends MapPlugin {

  constructor(map) {
    super("dispatch", map);


    this.defaultSettings = {
      missionTypeFilter: "",
      taskStateFilter: [],
      driverFilter: ""
    };

    this.styles = {
      td: "vertical-align: top; padding-right: 8px;",
      divider: `border: none; height: 1px; background: ${this.theme.palette.divider}; margin-bottom: 12px;`
    };

    this.addImage("circle");

    this.on("click", "mission-locations-widget", (ev) => {
      const { properties } = ev.features[0];
      this.emitToProvider("dispatchLayerClick", properties.id);
    });

    if (!this.mobile) {

      this.addPopup(
        "mission-popup",
        "mission-locations-widget",
        (_, properties) => this.renderPropertiesToPopup(properties)
      );

      this.addPopup(
        "mission-popup-cluster",
        "mission-clusters",
        (e, _, callback) => {

          const clusterId = e.features[0].properties.cluster_id;
          const point_count = e.features[0].properties.point_count;
          const clusterSource = this._map.getSource("mission-locations");

          clusterSource.getClusterLeaves(clusterId, point_count, 0, (err, aFeatures) => {
            if (err) return;

            callback(
              aFeatures
                .slice(0, 25)
                .map(f => this.renderPropertiesToPopup(f.properties))
                .join(`<hr style="${this.styles.divider}"/>`)
            );
          })
        },
        { contentInCallback: true }
      );

      this.on("mouseenter", "mission-locations-widget", (ev) => {
        const { properties } = ev.features[0];
        this.setHighlightLowContrast(properties.id);
        this.emitToProvider("dispatchLayerHover", properties.id);
      });

      this.on("mouseleave", "mission-locations-widget", (ev) => {
        this.setHighlightLowContrast();
        this.emitToProvider("dispatchLayerHover", null);
      });
    }
  }

  setHighlightLowContrast(taskId = "none") {
    this._map.setFilter("mission-locations-highlight-low-contrast", ["==", ["get", "id"], taskId]);
  }

  setHighlightHighContrast(taskId = "none") {
    this._map.setFilter("mission-locations-highlight-high-contrast", ["==", ["get", "id"], taskId]);
  }

  onMount() {
    const theme = this.theme;

    this._map.addSource("mission-locations", {
      type: "geojson",
      data: Route.GeoJson(
        this._ops.filteredTasks
          .filter(t => this.isValidTask(t))
          .map(t => this.convertToFeature(t))
      ),
      cluster: true,
      clusterRadius: 35
    });


    this._map.addLayer({
      id: "mission-locations-widget",
      type: "circle",
      slot: "elevation-3",
      source: "mission-locations",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": 18,
        "circle-color": ["get", "taskColor"],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#000",
      }
    });



    this._map.addLayer({
      id: "mission-locations-widget-text",
      source: "mission-locations",
      slot: "elevation-3",
      type: "symbol",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-field": ["get", "missionNumber"],
        "text-font": [
          "Open Sans Semibold",
          "Arial Unicode MS Bold"
        ],
        "text-size": 12,
        "text-anchor": "center"
      },
      paint: {
        "text-translate-anchor": "viewport",
        "text-translate": [0, 0]
      }
    });


    this._map.addLayer({
      id: "mission-locations-highlight-low-contrast",
      source: "mission-locations",
      slot: "elevation-3",
      type: "circle",
      filter: ["==", ["get", "id"], "none"],
      paint: {
        "circle-radius": 18,
        "circle-color": theme.palette.action.hover,
      }
    });

    this._map.addLayer({
      id: "mission-locations-highlight-high-contrast",
      source: "mission-locations",
      slot: "elevation-3",
      type: "circle",
      filter: ["==", ["get", "id"], "none"],
      paint: {
        "circle-radius": 17,
        "circle-color": theme.palette.action.hover,
        "circle-stroke-width": 4,
        "circle-stroke-color": theme.palette.info.main,
      }
    });

    this._map.addLayer({
      id: "mission-locations-badge",
      source: "mission-locations",
      slot: "elevation-3",
      type: "symbol",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "icon-image": "circle",
        "icon-size": 1.5,
        "icon-allow-overlap": true,
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-field": ["get", "taskNumber"],
        "text-font": [
          "Open Sans Semibold",
          "Arial Unicode MS Bold"
        ],
        "text-size": 10,
      },
      paint: {
        "icon-translate": [12, 12],
        "icon-translate-anchor": "viewport",
        "icon-color": "#000",
        "text-translate-anchor": "viewport",
        "text-translate": [12, 12],
        "text-color": "#FFFFFF"
      }
    });



    this._map.addLayer({
      id: "mission-locations-text",
      source: "mission-locations",
      slot: "elevation-3",
      type: "symbol",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-field": ["get", "title"],
        "text-font": [
          "Open Sans Semibold",
          "Arial Unicode MS Bold"
        ],
        "text-size": 12,
        "text-offset": [0, -1.9],
        "text-anchor": "bottom"
      },
      paint: {
        "text-halo-color": "#fff",
        "text-halo-width": 2
      }
    });



    this._map.addLayer({
      id: "mission-clusters",
      source: "mission-locations",
      slot: "elevation-3",
      type: "circle",
      filter: ["has", "point_count"],
      paint: {
        "circle-stroke-width": 2,
        "circle-stroke-color": "#000",
        "circle-color": [
          "step",
          ["get", "point_count"],
          theme.palette.info.light,
          4,
          theme.palette.info.main,
          9,
          theme.palette.info.dark,
        ],
        "circle-radius": [
          "step",
          ["get", "point_count"],
          20,
          100,
          30,
          750,
          40
        ]
      }
    });

    this._map.addLayer({
      id: "mission-clusters-text",
      source: "mission-locations",
      slot: "elevation-3",
      type: "symbol",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
      }
    });


  }

  onUnMount() {

    // remove layers
    this._map.removeLayer("mission-clusters");
    this._map.removeLayer("mission-clusters-text");
    this._map.removeLayer("mission-locations-text");
    this._map.removeLayer("mission-locations-badge");
    this._map.removeLayer("mission-locations-widget-text");
    this._map.removeLayer("mission-locations-widget");

    this._map.removeLayer("mission-locations-highlight-low-contrast");
    this._map.removeLayer("mission-locations-highlight-high-contrast");

    // remove source
    this._map.removeSource("mission-locations");
  }

  onUpdateDispatchData(filteredTasks, completedTasks) {
    this._map?.getSource("mission-locations")?.setData(
      Route.GeoJson(
        filteredTasks
          .filter(t => this.isValidTask(t))
          .map(t => this.convertToFeature(t))
      )
    );
  }

  isValidTask(task) {
    const location = task?.route?.waypoints[0];
    return !!task && location?.coordinates?.length >= 2;
  }

  convertToFeature(task) {

    const location = task.route.waypoints[0];
    const state = task.state;
    const driverAssignedState = task.execution.driverAssignedState;
    const details = task.missionDetails;

    const cause = details?.cause?.name || "";
    const tags = details?.tags?.map(t => `\n[${t.name}]`)?.join("");

    return GeoJsonPoint(
      location.coordinates,
      {
        id: task.id,
        title: `${!!cause ? `${cause} ` : ""}${tags}`,
        missionId: task.missionId,
        missionNumber: task.missionNumber,
        taskNumber: task.number,
        taskColor: GetTaskStateBackgroundColor(state, driverAssignedState),
        driverName: task.execution?.driver?.name,
        driverAssignedState,
        target: task.missionTarget,
        details: task.missionDetails,
        state
      }
    );
  }


  renderPropertiesToPopup(props) {
    const t = this.t;
    const theme = this.theme;

    // fetch target
    const target = typeof props.target === "string" ? JSON.parse(props.target) : props.target;
    const targetType = target.type;
    const targetDetails = targetType === 1 ? target.carDetails : (targetType === 2 ? target.boatDetails : null);
    const targetDescription = `${!!targetDetails ? `${targetDetails.make || "-"} ${targetDetails.model || "-"}, ${targetDetails.registration || "-"} ` : ""}`;

    // fetch description
    const details = typeof props.details === "string" ? JSON.parse(props.details) : props.details;
    const jobType = details.type === 0 ? "" : t(`mission:enums:type:${MissionEnumsLists.Type[details.type || 0]}`);
    const targetTypeName = target.type === 0 ? "" : t(`mission:enums:targetType:${MissionEnumsLists.TargetType[target?.type || 0]}`);
    const missionDescription = `${jobType} ${targetTypeName}`;

    return (
      `
      <strong>#${props.missionNumber}.${props.taskNumber} ${missionDescription}</strong>
      <table>
        <tr>
          <td style="${this.styles.td}">${t("home:cards:map:target")}</td><td style="color: ${theme.palette.text.secondary}; ${this.styles.td}">${targetDescription}</td>
        </tr>
        <tr>
          <td style="${this.styles.td}">${t("home:cards:map:driver")}</td><td style="color: ${theme.palette.text.secondary}; ${this.styles.td}">${props.driverName || "-"}</td>
        </tr>
        <tr>
          <td style="${this.styles.td}">${t("home:cards:map:state")}</td><td style="color: ${theme.palette.text.secondary}; ${this.styles.td}">${GetTaskDriverState(props.driverAssignedState, props.state, t)}</td>
        </tr>
      </table>
      `
    );
  }
}



export function DispatchLayerSettings() {
  const { t } = useTranslation();

  const {
    operationsMap,
    missionTypeFilter,
    setMissionTypeFilter,
    taskStateFilter,
    setTaskStateFilter,
    drivers,
    driverFilter,
    setDriverFilter,
  } = useOperationsMap();

  const [active, setActive] = useState(operationsMap.isPluginActive("dispatch"));

  useEffect(() => {
    const fn = () => {
      setActive(operationsMap.isPluginActive("dispatch"));
    };

    operationsMap.on("reset", fn);
    return () => operationsMap.off("reset", fn);
  }, [operationsMap]);

  const onHandleMissionType = useCallback((ev) => {
    setMissionTypeFilter(ev.target.value);
    operationsMap.updateSettings("dispatch", { missionTypeFilter: ev.target.value });
  }, [operationsMap]);

  const onHandleTaskState = useCallback((ev) => {
    let val = ev.target.value;

    if (!val.includes("operative")) {
      let ts = val.includes("") ? [] : val;
      setTaskStateFilter(ts);
      operationsMap.updateSettings("dispatch", { taskStateFilter: ts });
    }
  }, [operationsMap]);

  const onHandleDriver = useCallback((ev) => {
    setDriverFilter(ev.target.value);
    operationsMap.updateSettings("dispatch", { driverFilter: ev.target.value });
  }, [operationsMap]);

  // Handle plugin active state
  const onHandleAccordion = useCallback((_, val) => { // TODO handle this better, mount and unmount based on accordion open
    setActive(val);

    if (val) {
      operationsMap.activatePlugin("dispatch");
    } else {
      operationsMap.deactivatePlugin("dispatch");
    }
  }, [operationsMap]);

  const onSelectOperative = useCallback((ev) => {
    ev.preventDefault();
    onHandleTaskState({
      target: {
        value: [
          TaskEnums.State.None,
          TaskEnums.State.Created,
          TaskEnums.State.Assigned,
          TaskEnums.State.Responded,
          TaskEnums.State.Started,
          TaskEnums.State.Arrived,
          TaskEnums.State.Loaded,
          TaskEnums.State.ArrivedDest
        ]
      }
    });
  }, [onHandleTaskState])

  return (
    <LayerSettings title={t("operationsMap:layers:dispatch:title")} open={active} onChange={onHandleAccordion}>
      <Stack spacing={1}>


        <FormControl fullWidth size="small" variant="filled">
          <InputLabel>{t("operationsMap:layers:dispatch:taskState")}</InputLabel>
          <Select
            multiple
            onChange={onHandleTaskState}
            value={taskStateFilter}
            renderValue={(selected) => selected.map(value => `${t(`task:enums:state:${TaskEnumsLists.State[value]}`)} ${value === TaskEnums.State.Completed || value === TaskEnums.State.Acknowledged ? t("common:today") : ""}`).join(", ")}
          >
            <MenuItem value="">
              <IconButton>
                <ClearIcon />
              </IconButton>
              <ListItemText primary={t("common:all")} />
            </MenuItem>

            <MenuItem value="operative" onClick={onSelectOperative}>
              <IconButton>
                <FlagIcon />
              </IconButton>
              <ListItemText primary={t("operationsMap:layers:dispatch:activeTasks")} />
            </MenuItem>

            <Divider />

            {TaskEnumsLists.State.map((state, value) => (
              <MenuItem value={value} key={value}>
                <Checkbox checked={taskStateFilter.includes(value)} />
                <ListItemText primary={`${t(`task:enums:state:${state}`)} ${value === TaskEnums.State.Completed || value === TaskEnums.State.Acknowledged ? t("common:today") : ""}`} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>


        <FormControl fullWidth size="small" variant="filled">
          <InputLabel>{t("operationsMap:layers:dispatch:missionType")}</InputLabel>
          <Select
            onChange={onHandleMissionType}
            value={missionTypeFilter || ""}
          >
            <MenuItem value="">{t("common:all")}</MenuItem>
            {MissionEnumsLists.Type.map((type, value) => <MenuItem value={value} key={value}>{t(`mission:enums:type:${type}`)}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" variant="filled">
          <InputLabel>{t("operationsMap:layers:dispatch:driver")}</InputLabel>
          <Select
            onChange={onHandleDriver}
            value={driverFilter || ""}
          >
            <MenuItem value="">{t("common:all")}</MenuItem>
            {drivers.map((driver, value) => <MenuItem value={driver.id} key={value}>{driver.name}</MenuItem>)}
          </Select>
        </FormControl>

      </Stack>
    </LayerSettings>
  );
}



